import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

/**
 * OFF-FEED — global overlay state + focus-restore support.
 *
 * Consumers call:
 *   const { openOffFeed, registerBrainClick } = useOffFeed();
 *
 *   openOffFeed(triggerElement)                — opens the room
 *   registerBrainClick(triggerElement)         — register a click for the
 *      "3 clicks in 2s" easter egg; returns true only when the room opens
 *      (so the caller can suppress default navigation).
 *
 * The trigger element (a DOM node) is stored so focus can return to it on
 * close. The overlay reads it via `triggerRef.current`.
 */

const OffFeedContext = createContext(null);

const TRIPLE_CLICK_WINDOW_MS = 2000;
const TRIPLE_CLICK_COUNT = 3;

export function OffFeedProvider({ children }) {
    const [open, setOpen] = useState(false);
    const clicks = useRef([]);
    const triggerRef = useRef(null);

    const openOffFeed = useCallback((trigger) => {
        if (trigger instanceof HTMLElement) triggerRef.current = trigger;
        setOpen(true);
    }, []);

    const closeOffFeed = useCallback(() => setOpen(false), []);

    /** Returns true if this click completes a triple-click and the overlay is opened. */
    const registerBrainClick = useCallback((trigger) => {
        const now = Date.now();
        clicks.current = clicks.current.filter((t) => now - t < TRIPLE_CLICK_WINDOW_MS);
        clicks.current.push(now);
        if (clicks.current.length >= TRIPLE_CLICK_COUNT) {
            clicks.current = [];
            if (trigger instanceof HTMLElement) triggerRef.current = trigger;
            setOpen(true);
            return true;
        }
        return false;
    }, []);

    const value = useMemo(
        () => ({ open, openOffFeed, closeOffFeed, registerBrainClick, triggerRef }),
        [open, openOffFeed, closeOffFeed, registerBrainClick]
    );

    return <OffFeedContext.Provider value={value}>{children}</OffFeedContext.Provider>;
}

export function useOffFeed() {
    const ctx = useContext(OffFeedContext);
    if (!ctx) {
        return {
            open: false,
            openOffFeed: () => {},
            closeOffFeed: () => {},
            registerBrainClick: () => false,
            triggerRef: { current: null },
        };
    }
    return ctx;
}
